import type { GetQuantDirectoryTree } from '@trinity/chat/api/interfaces/projects.quant-folders';

export type QuantFolderTreeItem = {
  id: string;
  name: string;
  items: { id: number; name: string }[];
  children: QuantFolderTreeItem[];
};

export function mapNodeToFolderTreeItem(node: GetQuantDirectoryTree.QuantNode): QuantFolderTreeItem {
  const folder = node.item.item as GetQuantDirectoryTree.ProjectQuantAnalysisFolderRecord;

  const items = node.children
    .filter(child => child.item.type === 'analyses')
    .map(child => {
      const analysis = child.item.item as GetQuantDirectoryTree.ProjectQuantAnalysisItemRecord;
      return { id: analysis.id, name: analysis.name };
    });

  const children = node.children
    .filter(child => child.item.type === 'folder')
    .map(mapNodeToFolderTreeItem);

  return {
    id: String(folder.id),
    name: folder.name,
    items,
    children,
  };
}

export type FolderOption = {
  id: string | null;  // null represents root
  name: string;
  path: string;
};

// given a tree, we extract a list of all folders with their paths
export const extractFolderDestinations = (
  item: QuantFolderTreeItem,
  parentPath = '',
  rootName = '/',
): FolderOption[] => {
  const options: FolderOption[] = [];

  if (item.id === 'root') {
    // This is our synthetic root, process children directly without adding the root itself
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        options.push(...extractFolderDestinations(child, '', rootName));
      });
    }
    return options;
  }

  // build path for moving
  const currentPath = parentPath ? `${parentPath}/${item.name}` : item.name;

  // Add current folder as an option (skip only the very first root container)
  if (parentPath !== '' || item.name !== 'Root') {
    const displayPath = parentPath === '' ? `${rootName}/${item.name}` : `${rootName}/${currentPath}`;
    options.push({
      id: item.id,
      name: item.name,
      path: displayPath,
    });
  }

  // Recursively process children
  if (item.children && item.children.length > 0) {
    item.children.forEach(child => {
      const nextParentPath = (parentPath === '' && item.name === 'Root') ? '' : currentPath;
      options.push(...extractFolderDestinations(child, nextParentPath, rootName));
    });
  }

  return options;
};

// Helper to get all available folder destinations to move including root, excluding current location
export const getAllFolderDestinations = (
  data: QuantFolderTreeItem | null,
  currentPath?: string[],
  rootName = '/',
): FolderOption[] => {
  if (!data) {
    return [];
  }

  const rootOption: FolderOption = {
    id: null,
    name: rootName,
    path: rootName,
  };

  const folderOptions = extractFolderDestinations(data, '', rootName);

  // If we have a current path, filter out the current location
  if (currentPath && currentPath.length > 0) {
    // Handle the path formatting - if first element is '/', remove it
    const pathParts = currentPath[0] === '/' ? currentPath.slice(1) : currentPath;
    const currentLocationPath = pathParts.length > 0 ? `/${pathParts.join('/')}` : '/';
    const filteredOptions = folderOptions.filter(option => option.path !== currentLocationPath);

    if (currentLocationPath === '/') {
      return filteredOptions;
    }

    return [rootOption, ...filteredOptions];
  }

  return [rootOption, ...folderOptions];
};

export const getFolderPathById = (
  data: QuantFolderTreeItem | null,
  folderId: string | null,
  rootName = '/',
): string => {
  if (!folderId) {
    return rootName;
  }

  const allOptions = getAllFolderDestinations(data, undefined, rootName);
  const folder = allOptions.find(option => option.id === folderId);
  return folder?.path || rootName;
};

// Helper to find the current location of an item in the tree
export const findItemCurrentPath = (
  tree: GetQuantDirectoryTree.QuantNode[] | undefined,
  targetItemId: number,
  rootName = '/',
): string[] => {
  if (!tree) return [rootName];

  const findItemPath = (nodes: GetQuantDirectoryTree.QuantNode[], targetItemId: number, path: string[] = []): string[] | null => {
    for (const node of nodes) {
      if (node.item.type === 'analyses' && node.item.item.id === targetItemId) {
        if (path.length === 0) {
          return [rootName];
        } else {
          return [`${rootName}/${path.join('/')}`];
        }
      }

      if (node.item.type === 'folder') {
        const result = findItemPath(node.children, targetItemId, [...path, node.item.item.name]);
        if (result) return result;
      }
    }
    return null;
  };

  const path = findItemPath(tree, targetItemId);
  return path || [rootName];
};
